import React, { useEffect, useState } from "react";
import { getProfileId } from "../Services/GetService.tsx";
import useAccessToken from "../Custom/useAccessToken";
import AboutIntro from "./AboutIntro";
import { Skeleton } from "primereact/skeleton";
import { Person } from "@microsoft/mgt-react";
import { Card } from "primereact/card";

interface ProfileCardDashboardProps {
  profileID?: string | null;
  isOwnProfile?: boolean;
}

const ProfileCardDashboard: React.FC<ProfileCardDashboardProps> = ({
  profileID,
  isOwnProfile,
}) => {
  const { accessToken } = useAccessToken();

  const [loading, setLoading] = useState(false);

  // Define the Profile model
  interface Profile {
    profileId: string;
    entraId: string;
    name: string;
    email: string;
    designation: string;
    location: string;
    company: string;
    department: string;
    profileShortCode: string;
    linkedIn: string;
    introduction: string;
    managerId: string | null;
    manager: string;
  }

  const [profile, setProfile] = React.useState<Profile | null>(null);

  useEffect(() => {
    const fetchProfileData = async () => {
      if (!accessToken || !profileID) return;

      // Pass profileID to getProfileDetails if needed

      try {
        setLoading(true);
        const token = accessToken;
        const profileData = await getProfileId(token, profileID);
        setProfile(profileData);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching profile:", err);
        setProfile(null);
      } finally {
        setLoading(false);
      }
    };

    fetchProfileData();
  }, [accessToken, profileID]);

  return (
    <>
      {loading ? (
        <Card className="mb-3 shadow-1">
          <div className="flex align-items-center justify-content-between w-full">
            <div className="flex align-items-center">
              <Skeleton shape="circle" size="80px" />
              <div className="ml-3 line-height-2">
                <Skeleton width="220px" height="1.4rem" className="mb-2" />
                <Skeleton width="160px" height="1rem" />
              </div>
            </div>
            <div className="flex align-items-center text-base font-medium">
              <Skeleton width="120px" height="1rem" className="mr-3" />
              <Skeleton width="100px" height="1rem" />
            </div>
          </div>
        </Card>
      ) : (
        <>
          <Card className="mb-3 shadow-1">
            <div className="flex align-items-center justify-content-between w-full">
              <div className="flex align-items-center">
                {profile?.entraId ? (
                  <Person
                    userId={profile.entraId}
                    personCardInteraction="hover"
                    showPresence={true}
                    avatarSize="large"
                  />
                ) : null}
                <div className="ml-3 line-height-2">
                  <div className="text-900 text-lg font-semibold">
                    {profile?.name}
                  </div>
                  <div className="text-base">{profile?.designation}</div>
                </div>
              </div>
              <div className="flex align-items-center text-base font-medium">
                <span>{profile?.department}</span>
                <span className="mx-2" aria-hidden="true">
                  |
                </span>
                <span>{profile?.location}</span>
              </div>
            </div>
          </Card>
          {(profile?.introduction || isOwnProfile) && (
              <AboutIntro
                introduction={profile?.introduction}
                profileId={profileID}
                isOwnProfile={isOwnProfile}
              />
          )}
        </>
      )}
    </>
  );
};

export default ProfileCardDashboard;
